import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LibrarySection.css';

/**
 * Unified Library section with Company Profile slider + external link cards
 * Used on HomePage and NewGraduatePage with enhanced design from NewGraduate version
 */
const LibrarySection = ({ companyName = 'FLARETECH', moreLink = '/about/more', moreLinkText = 'もっと知るリンク集' }) => {
  useEffect(() => {
    // Load SpeakerDeck embed script
    const script = document.createElement('script');
    script.src = '//speakerdeck.com/assets/embed.js';
    script.async = true;
    script.charset = 'utf-8';
    document.body.appendChild(script);

    // Fix iframe title after script loads
    const fixIframeTitle = () => {
      const iframe = document.querySelector('.speakerdeck-embed iframe');
      if (iframe && iframe.getAttribute('title') === 'null') {
        iframe.setAttribute('title', 'FLARETECH Company Profile - 会社紹介スライド');
      }
    };

    // Try to fix title after script loads with multiple attempts
    const checkInterval = setInterval(() => {
      const iframe = document.querySelector('.speakerdeck-embed iframe');
      if (iframe) {
        fixIframeTitle();
        clearInterval(checkInterval);
      }
    }, 100);

    // Clear interval after 5 seconds to avoid infinite checking
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 5000);

    return () => {
      // Cleanup
      clearInterval(checkInterval);
      clearTimeout(timeout);
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <section className="library-section">
      <div className="library-container">
        <h2 className="library-section-title">{companyName} Library</h2>
        
        <div className="library-content">
          {/* Left Column - Slide Player */}
          <div className="library-main">
            <div className="library-player">
              <div className="library-slide-viewer">
                <div 
                  className="speakerdeck-embed" 
                  data-id="e791daf4919f4d24abc17622339cac7e" 
                  data-ratio="1.7790157845868153"
                ></div>
              </div>
              <div className="library-player-info">
                <h4 className="player-title">Company Profile</h4>
                <p className="player-desc">
                  フレアテックの基本情報や働く魅力について知っておくといい<br />
                  ポイントをスライドでまとめています。
                </p>
                <div className="player-actions">
                  <a 
                    href="https://speakerdeck.com/flaretech/marvelzhu-shi-hui-she-corporate-profile" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="player-view-button"
                  >
                    View Larger
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                    </svg>
                  </a>
                  <button 
                    className="player-share-button"
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: 'FLARETECH Corporate Profile',
                          text: 'FLARETECHの会社紹介資料をご覧ください',
                          url: 'https://speakerdeck.com/flaretech/marvelzhu-shi-hui-she-corporate-profile'
                        });
                      } else {
                        navigator.clipboard.writeText('https://speakerdeck.com/flaretech/marvelzhu-shi-hui-she-corporate-profile');
                        alert('リンクをコピーしました');
                      }
                    }}
                    aria-label="シェア"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Link Cards Grid */}
          <div className="library-links">
            <div className="library-links-grid">
              {/* YouTube */}
              <a href="https://www.youtube.com/channel/UCbLZK0VW8Kf0PtzuoPn5vXg" className="library-link-card" target="_blank" rel="noopener noreferrer">
                <div className="link-card-logo">
                  <div className="youtube-logo">
                    <svg viewBox="0 0 24 24" fill="#FF0000">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                    <span>YouTube</span>
                  </div>
                </div>
                <h5 className="link-card-title">FLARETECHのYoutube</h5>
                <p className="link-card-desc">社員や仕事について動画でご紹介。職種ごとのリアルをメンバーの対話から知ることができます。</p>
              </a>

              {/* X (Twitter) */}
              <a href="https://twitter.com/FLARETECH_saiyo" className="library-link-card" target="_blank" rel="noopener noreferrer">
                <div className="link-card-logo">
                  <svg className="x-logo" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </div>
                <h5 className="link-card-title">【公式】FLARETECH株式会社 新卒採用X</h5>
                <p className="link-card-desc">フレアテックの「今」を発信中。イベントや最新記事などを、いち早くご確認いただけます。</p>
              </a>

              {/* note */}
              <a href="https://note.com/flaretech_it" className="library-link-card" target="_blank" rel="noopener noreferrer">
                <div className="link-card-logo">
                  <span className="logo-text note-logo" aria-label="note">no<span className="note-plus">+</span>e</span>
                </div>
                <h5 className="link-card-title">FLARETECH公式note</h5>
                <p className="link-card-desc">各メンバーが公開した記事などを、マガジン形式でまとめてご紹介しています。</p>
              </a>

              {/* FLARETECH Blog - On Hold */}
              {/* <a href="https://goodpatch.com/blog" className="library-link-card" target="_blank" rel="noopener noreferrer">
                <div className="link-card-logo">
                  <span className="logo-text blog-logo">FLARETECH Blog</span>
                </div>
                <h5 className="link-card-title">フレアテックブログ</h5>
                <p className="link-card-desc">2012年から運営・1,200以上の記事を公開中。ナレッジから人、カルチャーまでご覧いただけます。</p>
              </a> */}
            </div>
          </div>
        </div>

        <div className="library-footer">
          <Link to={moreLink} className="library-more-link">
            {moreLinkText}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LibrarySection;
